/**
 * Web widget registry — name → React component, schemas imported from
 * @rmint/widgets/registry. Single source of truth for the (name, schema)
 * pair lives in packages/widgets; this file binds components to names.
 *
 * Adding a widget:
 *   1. Author packages/widgets/src/{name}/schema.ts (the agent-side contract)
 *   2. Implement apps/web/src/components/widgets/{name}.tsx (the React component)
 *   3. Add the import + the entry below
 *
 * Skeleton widgets stay registered with their existing skeleton components;
 * K.4 does not implement real component bodies for the 9 placeholder
 * investor widgets. K.5/K.6 swap real implementations in.
 */
import type { WidgetComponent } from '@rmint/shell';
import { widgetRegistry as schemaRegistry, type WidgetName } from '@rmint/widgets/registry';
import {
  AwaitingFinalizationSkeleton,
  CreateSubscriptionSkeleton,
  CustodySafeSkeleton,
  FormInvestSkeleton,
  FundWalletSkeleton,
  KycUploadSkeleton,
  PaymentSkeleton,
  ReviewSkeleton,
  SubscriptionSignSkeleton,
  TokenDeliverySkeleton,
  TxSignSkeleton,
  WalletOverviewSkeleton,
} from './skeletons';
import { KycStripeIdentity } from './kyc-stripe-identity';
import { SubscribeKycAction } from './subscribe-kyc-action';
import { SubscribeAllocateForm } from './subscribe-allocate-form';
import { SubscribeSignForm } from './subscribe-sign-form';
import { SubscribeFundFlow } from './subscribe-fund-flow';
import { SubscribeMintConfirmation } from './subscribe-mint-confirmation';

export type { WidgetProps, WidgetComponent } from '@rmint/shell';

// Widget components receive the directive's props as string-typed
// HTML attributes plus injected runtime props (`WidgetProps`, sourced
// from @rmint/shell so the renderer in shell and this concrete registry
// agree on the prop shape — re-exported above for the widget files'
// `import type { WidgetProps } from './registry'`).

/**
 * Components keyed by the widget names in @rmint/widgets/registry.
 * Partial: K.4 introduced 6 stub schemas (chart, form-expense, form-invest,
 * form-sale, create-subscription, revenue-claim) with rolesAllowedToEmit=[]
 * — no agent emits them, so binding components is YAGNI. lint:cells +
 * registry schema lookup catch any cell that references a missing component.
 * Misspelled names still fail TS at compile time because WidgetName is
 * the keyof type — Partial just allows the missing-key case.
 */
const agentEmittableComponents: Partial<Record<WidgetName, WidgetComponent>> = {
  'tx-sign': TxSignSkeleton,
  'kyc-upload': KycUploadSkeleton,
  'kyc-form': KycStripeIdentity,
  payment: PaymentSkeleton,
  'fund-wallet': FundWalletSkeleton,
  review: ReviewSkeleton,
  'subscription-sign': SubscriptionSignSkeleton,
  'custody-safe': CustodySafeSkeleton,
  'awaiting-finalization': AwaitingFinalizationSkeleton,
  'token-delivery': TokenDeliverySkeleton,
  'subscribe-kyc-action': SubscribeKycAction,
  'subscribe-allocate-form': SubscribeAllocateForm,
  'subscribe-sign-form': SubscribeSignForm,
  'subscribe-fund-flow': SubscribeFundFlow,
  'subscribe-mint-confirmation': SubscribeMintConfirmation,
};

/**
 * Web-only widgets — never agent-emitted (NOT in @rmint/widgets/registry),
 * only used by static Phase 5/6 cells. They live in this file with their
 * own (no schema) entries.
 */
const webOnlyComponents: Record<string, WidgetComponent> = {
  'form-invest': FormInvestSkeleton,
  'wallet-overview': WalletOverviewSkeleton,
  'create-subscription': CreateSubscriptionSkeleton,
};

export const widgetRegistry: Record<string, WidgetComponent> = {
  ...agentEmittableComponents,
  ...webOnlyComponents,
};

/** Schemas re-exported for runtime mount-time validation (defense in depth). */
export { schemaRegistry };
